import { useTranslation, useLocale } from '@parentpath/i18n';
import { ArrowLeft, Calendar, Baby, Heart, AlertTriangle } from 'lucide-react';

// Baby names can be either strings or objects with name property
type BabyNameEntry = string | { name: string; gender?: string };

interface PregnancyDetails {
  expectedDueDate: string;
  childCount: number;
  babyNames: BabyNameEntry[];
  inviterName: string;
  completionType?: 'birth' | 'ended' | null;
  actualBirthDate?: string | null;
  babyData?: Record<string, unknown> | null;
}

interface PregnancyDetailsStepProps {
  details: PregnancyDetails;
  onNext: () => void;
  onBack: () => void;
  warningMessage?: string | null;
}

export function PregnancyDetailsStep({ details, onNext, onBack, warningMessage }: PregnancyDetailsStepProps) {
  const { t } = useTranslation('onboarding');
  const { locale } = useLocale();

  const isBabyBorn = details.completionType === 'birth' && !!details.actualBirthDate;

  // Format a date string for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(locale === 'en' ? 'en-US' : 'de-DE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Calculate SSW
  const calculateSSW = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const remainingWeeks = Math.floor(diffDays / 7);
    const currentSSW = 40 - remainingWeeks;
    return Math.max(1, Math.min(42, currentSSW));
  };

  // Calculate baby age in days
  const calculateBabyAgeDays = (birthDateStr: string) => {
    const birthDate = new Date(birthDateStr);
    const now = new Date();
    return Math.floor((now.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const ssw = !isBabyBorn ? calculateSSW(details.expectedDueDate) : null;
  const babyAgeDays = isBabyBorn ? calculateBabyAgeDays(details.actualBirthDate!) : null;

  // Extract baby names from baby_data.babies if available, fall back to babyNames
  const getBabyNameStrings = (): string[] => {
    // Try baby_data.babies first (used in baby mode)
    if (details.babyData && typeof details.babyData === 'object') {
      const babies = (details.babyData as { babies?: Array<{ name?: string }> }).babies;
      if (Array.isArray(babies)) {
        const names = babies
          .map(b => b.name)
          .filter((name): name is string => !!name && name.trim() !== '');
        if (names.length > 0) return names;
      }
    }
    // Fall back to babyNames
    return (details.babyNames || [])
      .map((entry) => (typeof entry === 'string' ? entry : entry?.name))
      .filter((name): name is string => !!name && name.trim() !== '');
  };

  const babyNameStrings = getBabyNameStrings();
  const hasBabyNames = babyNameStrings.length > 0;

  // Child count text
  const childCountText = () => {
    switch (details.childCount) {
      case 1:
        return t('pregnancyDetails.singleton');
      case 2:
        return t('pregnancyDetails.twins');
      case 3:
        return t('pregnancyDetails.triplets');
      case 4:
        return t('pregnancyDetails.quadruplets');
      default:
        return t('pregnancyDetails.childrenCount', { count: details.childCount });
    }
  };

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pink-500/20 rounded-full flex items-center justify-center">
        {isBabyBorn ? (
          <Baby className="w-8 h-8 text-pink-400" />
        ) : (
          <Heart className="w-8 h-8 text-pink-400" />
        )}
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">
        {isBabyBorn ? t('pregnancyDetails.titleBaby') : t('pregnancyDetails.title')}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-6 text-center">
        {isBabyBorn
          ? t('pregnancyDetails.descriptionBaby', { name: details.inviterName })
          : t('pregnancyDetails.description', { name: details.inviterName })}
      </p>

      {/* Details Card */}
      <div className="bg-white/10 rounded-[15px] border border-white/20 p-6 mb-6 space-y-4">
        {/* Date row: Due Date or Birth Date */}
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-pp-orange" />
          </div>
          <div>
            {isBabyBorn ? (
              <>
                <p className="text-sm text-white/50">{t('pregnancyDetails.birthDate')}</p>
                <p className="font-semibold text-white">{formatDate(details.actualBirthDate!)}</p>
                <p className="text-sm text-pp-orange">
                  {t('pregnancyDetails.babyAge', { days: babyAgeDays })}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-white/50">{t('pregnancyDetails.dueDate')}</p>
                <p className="font-semibold text-white">{formatDate(details.expectedDueDate)}</p>
                <p className="text-sm text-pp-orange">SSW {ssw}</p>
              </>
            )}
          </div>
        </div>

        {/* Baby Names (priority) or Child Count as fallback */}
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Baby className="w-5 h-5 text-pink-400" />
          </div>
          <div>
            {hasBabyNames ? (
              <>
                <p className="text-sm text-white/50">
                  {babyNameStrings.length === 1 ? t('pregnancyDetails.nameSingular') : t('pregnancyDetails.namePlural')}
                </p>
                <p className="font-semibold text-white">{babyNameStrings.join(' & ')}</p>
              </>
            ) : (
              <>
                <p className="text-sm text-white/50">{t('pregnancyDetails.pregnancy')}</p>
                <p className="font-semibold text-white">{childCountText()}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Cancellation Warning */}
      {warningMessage && (
        <div className="bg-amber-500/20 border border-amber-500/30 rounded-[10px] p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200">{warningMessage}</p>
          </div>
        </div>
      )}

      {/* Info Text */}
      <p className="text-sm text-white/50 text-center mb-6">
        {isBabyBorn ? t('pregnancyDetails.infoBaby') : t('pregnancyDetails.info')}
      </p>

      {/* CTA */}
      <button
        onClick={onNext}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors"
      >
        {t('common.next')}
      </button>
    </div>
  );
}
